/**
 * scRNA-seq pipeline helpers.
 *
 * Keep the preview UI data-driven: derive the pipeline badge and
 * description2 from the selected analysis service in product_line.config.json.
 */

const normalizeServiceCatalog = (serviceCatalog = {}) => {
  if (!serviceCatalog || typeof serviceCatalog !== 'object') {
    return [];
  }

  return Object.values(serviceCatalog).flatMap(category => (
    Array.isArray(category) ? category : []
  ));
};

export const findServiceConfigByValue = (serviceCatalog = {}, serviceName = '') => {
  if (!serviceName) return null;

  return normalizeServiceCatalog(serviceCatalog).find(item => item?.value === serviceName) || null;
};

const getExplicitScRnaLabels = (serviceConfig = {}) => {
  const binding = serviceConfig?.binding || {};
  const platformLabel = serviceConfig.scRNAPlatformLabel || binding.scRNAPlatformLabel || '';
  const pipelineLabel = serviceConfig.scRNAPipelineLabel || binding.scRNAPipelineLabel || '';

  if (!platformLabel || !pipelineLabel) {
    return null;
  }

  return {
    platformLabel: String(platformLabel).trim(),
    pipelineLabel: String(pipelineLabel).trim()
  };
};

const normalizeScRnaServiceDescriptor = (serviceName = '') => {
  const match = String(serviceName).trim().match(/scRNAseq-(.+)$/i);
  if (!match) return null;

  const descriptor = match[1].trim();
  const separatorIndex = descriptor.indexOf('_');
  if (separatorIndex < 0) return null;

  const platformToken = descriptor.slice(0, separatorIndex).trim();
  const pipelineToken = descriptor.slice(separatorIndex + 1).trim();

  if (!platformToken || !pipelineToken) return null;

  return { platformToken, pipelineToken };
};

const inferPlatformLabel = (platformToken = '') => {
  const normalized = String(platformToken).toLowerCase().replace(/[^a-z0-9]/g, '');

  if (normalized === '10x' || normalized === '10xgenomics') {
    return '10X Genomics';
  }

  if (normalized === 'singleron') {
    return 'Singleron';
  }

  return platformToken || null;
};

const inferPipelineLabel = (pipelineToken = '') => {
  const label = String(pipelineToken)
    .replace(/\s*[（(].*$/, '')
    .trim();

  return label || null;
};

export const getScRNAseqPipelineInfo = ({
  serviceItems = [],
  serviceCatalog = {}
} = {}) => {
  const items = Array.isArray(serviceItems) ? serviceItems : [];

  const selectedService = items
    .flatMap(item => (Array.isArray(item?.services) ? item.services : []))
    .find(svc => {
      const serviceName = svc?.service;
      if (!serviceName) return false;

      const serviceConfig = findServiceConfigByValue(serviceCatalog, serviceName);
      return serviceConfig?.isScRNAseq === true;
    });

  if (!selectedService?.service) return null;

  const serviceConfig = findServiceConfigByValue(serviceCatalog, selectedService.service);
  if (!serviceConfig?.isScRNAseq) return null;

  const explicitLabels = getExplicitScRnaLabels(serviceConfig);
  if (explicitLabels) {
    return {
      serviceValue: selectedService.service,
      platformLabel: explicitLabels.platformLabel,
      pipelineLabel: explicitLabels.pipelineLabel,
      description2: serviceConfig.description2 || ''
    };
  }

  const descriptor = normalizeScRnaServiceDescriptor(selectedService.service);
  if (!descriptor) return null;

  const platformLabel = inferPlatformLabel(descriptor.platformToken);
  const pipelineLabel = inferPipelineLabel(descriptor.pipelineToken);
  if (!platformLabel || !pipelineLabel) return null;

  return {
    serviceValue: selectedService.service,
    platformLabel,
    pipelineLabel,
    description2: serviceConfig.description2 || ''
  };
};
